import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Badge, // Add this import
} from "@mui/material";
import { FilterList, Sort, Clear, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import {
  FindAllTicketsQueryFiltersSortByEnum,
  FindAllTicketsQueryFiltersSortOrderEnum,
  FindAllTicketsQueryFiltersTicketStatusEnum,
  FindAllTicketsQueryFiltersTicketTypeEnum,
  TicketFlatFilterDto,
} from "../../api-client";
import { mobiliTheme } from "../../themes/mobiliTheme";
import type { TicketFilterState } from "./hooks/useTicketFilterFlatState";

interface TicketFiltersProps {
  filterState: TicketFilterState;
  showFilters: boolean;
  onTypeChange: (type: FindAllTicketsQueryFiltersTicketTypeEnum[]) => void;
  onStatusChange: (status: FindAllTicketsQueryFiltersTicketStatusEnum[]) => void;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  onSortChange: (sortBy: FindAllTicketsQueryFiltersSortByEnum) => void;
  onResetFilters: () => void;
  onToggleFilters: () => void;
  translatedFlatFilters?: TicketFlatFilterDto;
  /**
   * The consumer's ACTUAL default sort. The active-filter badge only counts
   * the sort when it differs from this — previously the default was hardcoded
   * here as createdAt+DESC while TicketOverview's real default is
   * createdAt+ASC (deliberate, MOB-514), so the badge showed a permanent "1".
   */
  defaultSort?: {
    sortBy: FindAllTicketsQueryFiltersSortByEnum;
    sortOrder: FindAllTicketsQueryFiltersSortOrderEnum;
  };
}

const TicketFilters: React.FC<TicketFiltersProps> = ({
  filterState,
  showFilters,
  onTypeChange,
  onStatusChange,
  onFromDateChange,
  onToDateChange,
  onSortChange,
  onResetFilters,
  onToggleFilters,
  translatedFlatFilters,
  defaultSort = {
    sortBy: FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
    sortOrder: FindAllTicketsQueryFiltersSortOrderEnum.Asc,
  },
}) => {
  const handleTypeChange = (
    event: SelectChangeEvent<FindAllTicketsQueryFiltersTicketTypeEnum[]>
  ) => {
    const value = event.target.value;
    onTypeChange(
      typeof value === "string"
        ? (value.split(",") as FindAllTicketsQueryFiltersTicketTypeEnum[])
        : value
    );
  };

  const handleStatusChange = (event: SelectChangeEvent<typeof filterState.status>) => {
    const value = event.target.value;
    onStatusChange(
      typeof value === "string"
        ? (value.split(",") as FindAllTicketsQueryFiltersTicketStatusEnum[])
        : value
    );
  };

  // Handle removing individual type chips
  const handleDeleteType = (typeToDelete: FindAllTicketsQueryFiltersTicketTypeEnum) => {
    const newTypes = filterState.type.filter((type) => type !== typeToDelete);
    onTypeChange(newTypes);
  };

  // Handle removing individual status chips
  const handleDeleteStatus = (statusToDelete: FindAllTicketsQueryFiltersTicketStatusEnum) => {
    const newStatuses = filterState.status.filter((status) => status !== statusToDelete);
    onStatusChange(newStatuses);
  };

  // Function to count active filters
  const getActiveFiltersCount = () => {
    let count = 0;

    // Count type filters
    if (filterState.type && filterState.type.length > 0) {
      count += filterState.type.length;
    }

    // Count status filters (only if not default)
    if (filterState.status && filterState.status.length > 0) {
      // You might want to exclude default status from count
      // Adjust this logic based on your requirements
      count += filterState.status.length;
    }

    // Count date filters
    if (filterState.fromDate) {
      count += 1;
    }

    if (filterState.toDate) {
      count += 1;
    }

    // Count sort only when it differs from the consumer's actual default —
    // the default state must never produce a badge.
    if (
      filterState.sortBy !== defaultSort.sortBy ||
      filterState.sortOrder !== defaultSort.sortOrder
    ) {
      count += 1;
    }

    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box>
        {/* Filter Toggle Button with Badge */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Badge
            badgeContent={activeFiltersCount}
            color="primary"
            invisible={activeFiltersCount === 0} // Hide badge when no filters
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: mobiliTheme.palette.secondary.main,
                color: "white",
                fontWeight: "bold",
                fontSize: "0.75rem",
                minWidth: "18px",
                height: "18px",
              },
            }}
          >
            <IconButton
              onClick={onToggleFilters}
              sx={{
                backgroundColor: showFilters
                  ? mobiliTheme.palette.primary.main
                  : mobiliTheme.palette.grey[200],
                color: showFilters ? "white" : "inherit",
                "&:hover": {
                  backgroundColor: showFilters
                    ? mobiliTheme.palette.primary.dark
                    : mobiliTheme.palette.grey[300],
                },
              }}
            >
              <FilterList />
            </IconButton>
          </Badge>
        </Box>

        {/* Filters Section - overlays content when expanded */}
        <Collapse in={showFilters} timeout="auto" unmountOnExit>
          <Box
            sx={{
              padding: 2,
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              marginTop: 1,
              boxShadow: 3, // Add shadow to make it stand out over content
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle2">Filter & Sortierung</Typography>
              <IconButton
                size="small"
                aria-label="Filter schließen"
                onClick={onToggleFilters}
                sx={{ color: mobiliTheme.palette.text.secondary }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Grid container spacing={2} alignItems="flex-start">
              {/* Type Filter */}
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Typ</InputLabel>
                  <Select
                    multiple
                    value={filterState.type}
                    label="Typ"
                    onChange={handleTypeChange}
                    displayEmpty
                    renderValue={(selected) => (
                      <Typography
                        variant="body2"
                        sx={{ color: selected.length === 0 ? "text.disabled" : "inherit" }}
                      >
                        {selected.length === 0
                          ? "Alle Typen"
                          : `${selected.length} Typ(en) ausgewählt`}
                      </Typography>
                    )}
                  >
                    {translatedFlatFilters?.ticketType?.map((ticketType) => (
                      <MenuItem
                        key={ticketType.value}
                        value={ticketType.value}
                        sx={{
                          backgroundColor: filterState.type.includes(
                            ticketType.value as FindAllTicketsQueryFiltersTicketTypeEnum
                          )
                            ? mobiliTheme.palette.secondary.light // Changed from primary to secondary
                            : "transparent",
                          color: filterState.type.includes(
                            ticketType.value as FindAllTicketsQueryFiltersTicketTypeEnum
                          )
                            ? "white"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: filterState.type.includes(
                              ticketType.value as FindAllTicketsQueryFiltersTicketTypeEnum
                            )
                              ? mobiliTheme.palette.secondary.main // Changed from primary to secondary
                              : mobiliTheme.palette.action.hover,
                          },
                          "&.Mui-selected": {
                            backgroundColor: mobiliTheme.palette.secondary.light, // Changed from primary to secondary
                            color: "white",
                            "&:hover": {
                              backgroundColor: mobiliTheme.palette.secondary.main, // Changed from primary to secondary
                            },
                          },
                        }}
                      >
                        {ticketType.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Type Chips - Outside the Select */}
                {filterState.type.length && translatedFlatFilters?.ticketType ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    {filterState.type.map((value) => (
                      <Chip
                        key={value}
                        label={
                          translatedFlatFilters?.ticketType.filter(
                            (ttype) => ttype.value == value
                          )?.[0].label || value
                        }
                        size="small"
                        onDelete={() => handleDeleteType(value)}
                        deleteIcon={<Clear sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          backgroundColor: mobiliTheme.palette.secondary.light, // Changed from primary to secondary
                          color: "white",
                          "& .MuiChip-deleteIcon": {
                            color: "white",
                            fontSize: "16px",
                            "&:hover": {
                              color: mobiliTheme.palette.error.main,
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={filterState.status}
                    label="Status"
                    onChange={handleStatusChange}
                    displayEmpty
                    renderValue={(selected) => (
                      <Typography
                        variant="body2"
                        sx={{ color: selected.length === 0 ? "text.disabled" : "inherit" }}
                      >
                        {selected.length === 0
                          ? "Alle Status"
                          : `${selected.length} Status ausgewählt`}
                      </Typography>
                    )}
                  >
                    {/* Object.values(FindAllTicketsQueryFiltersTicketStatusEnum) */}
                    {translatedFlatFilters?.statuses?.map((status) => (
                      <MenuItem
                        key={status.value}
                        value={status.value}
                        sx={{
                          backgroundColor: filterState.status.includes(
                            status.value as FindAllTicketsQueryFiltersTicketStatusEnum
                          )
                            ? mobiliTheme.palette.secondary.light // Changed from primary to secondary
                            : "transparent",
                          color: filterState.status.includes(
                            status.value as FindAllTicketsQueryFiltersTicketStatusEnum
                          )
                            ? "white"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: filterState.status.includes(
                              status.value as FindAllTicketsQueryFiltersTicketStatusEnum
                            )
                              ? mobiliTheme.palette.secondary.main // Changed from primary to secondary
                              : mobiliTheme.palette.action.hover,
                          },
                          "&.Mui-selected": {
                            backgroundColor: mobiliTheme.palette.secondary.light, // Changed from primary to secondary
                            color: "white",
                            "&:hover": {
                              backgroundColor: mobiliTheme.palette.secondary.main, // Changed from primary to secondary
                            },
                          },
                        }}
                      >
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Status Chips - Outside the Select */}
                {filterState.status.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    {filterState.status.map((value) => (
                      <Chip
                        key={value}
                        label={
                          translatedFlatFilters?.statuses.filter(
                            (status) => status.value === value
                          )?.[0].label || value
                        }
                        size="small"
                        onDelete={() => handleDeleteStatus(value)}
                        deleteIcon={<Clear sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          backgroundColor: mobiliTheme.palette.secondary.light, // Changed from primary to secondary
                          color: "white",
                          "& .MuiChip-deleteIcon": {
                            color: "white",
                            fontSize: "16px",
                            "&:hover": {
                              color: mobiliTheme.palette.error.main,
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              {/* From Date */}
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DatePicker
                    label="Von Datum"
                    format="dd.MM.yyyy"
                    value={filterState.fromDate}
                    onChange={onFromDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { flexGrow: 1, padding: 0, margin: 0 },
                      },
                    }}
                  />
                  {filterState.fromDate && (
                    <IconButton
                      size="small"
                      onClick={() => onFromDateChange(null)}
                      sx={{
                        color: mobiliTheme.palette.text.secondary,
                        "&:hover": {
                          color: mobiliTheme.palette.error.main,
                        },
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DatePicker
                    label="Bis Datum"
                    format="dd.MM.yyyy"
                    value={filterState.toDate}
                    onChange={onToDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { flexGrow: 1, padding: 0, margin: 0 },
                      },
                    }}
                  />
                  {filterState.toDate && (
                    <IconButton
                      size="small"
                      onClick={() => onToDateChange(null)}
                      sx={{
                        color: mobiliTheme.palette.text.secondary,
                        "&:hover": {
                          color: mobiliTheme.palette.error.main,
                        },
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              {/* Sorting */}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary", mb: 0.5 }}
                >
                  Sortieren nach
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Button
                    variant={
                      filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.Priority
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    onClick={() => onSortChange(FindAllTicketsQueryFiltersSortByEnum.Priority)}
                    color="secondary"
                    sx={{ flex: "1 1 auto", minWidth: 120, whiteSpace: "nowrap" }}
                  >
                    Priorität{" "}
                    {filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.Priority &&
                      (filterState.sortOrder === FindAllTicketsQueryFiltersSortOrderEnum.Asc
                        ? "↑"
                        : "↓")}
                  </Button>

                  <Button
                    variant={
                      filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.CreatedAt
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    onClick={() => onSortChange(FindAllTicketsQueryFiltersSortByEnum.CreatedAt)}
                    color="secondary"
                    sx={{ flex: "1 1 auto", minWidth: 120, whiteSpace: "nowrap" }}
                  >
                    Datum{" "}
                    {filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.CreatedAt &&
                      (filterState.sortOrder === FindAllTicketsQueryFiltersSortOrderEnum.Asc
                        ? "↑"
                        : "↓")}
                  </Button>

                  <Button
                    variant={
                      filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.Bsnr
                        ? "contained"
                        : "outlined"
                    }
                    size="small"
                    onClick={() => onSortChange(FindAllTicketsQueryFiltersSortByEnum.Bsnr)}
                    color="secondary"
                    sx={{ flex: "1 1 auto", minWidth: 140, whiteSpace: "nowrap" }}
                  >
                    Praxis{" "}
                    {filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.Bsnr &&
                      (filterState.sortOrder === FindAllTicketsQueryFiltersSortOrderEnum.Asc
                        ? "↑"
                        : "↓")}
                  </Button>
                </Box>
              </Grid>

              {/* Clear Filters */}
              <Grid item xs={12}>
                <Button
                  variant="text"
                  size="small"
                  onClick={onResetFilters}
                  startIcon={<Clear />}
                  sx={{ color: mobiliTheme.palette.text.secondary }}
                >
                  Alle Filter zurücksetzen
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
};

export default TicketFilters;
